import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { TdDialogService } from '@covalent/core/dialogs';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import { map, share, switchMap, take, tap } from 'rxjs/operators';
import { AircraftMakeModelHttpClient } from 'src/app/shared/flight-log-api/aircraft-make-models/aircraft-make-models-client.service';
import { AssignmentToDutyHttpClient } from 'src/app/shared/flight-log-api/assignment-to-duties/assignment-to-duties-client.service';
import { FlLoadingService } from 'src/lib/fl-loading/fl-loading.service';
import { AppState } from 'src/store';
import { AssignmentToDutyEntity } from 'src/store/assignment-to-duty/assignment-to-duty.model';
import { sleep } from 'src/util/async';
import { AssignmentToDutyViewModel } from 'src/view-models/assignment-to-duty-view-model';
import { PilotEmploymentRecordAssignmentToDutyAddDialogComponent } from '../pilot-employment-record-assignment-to-duty-add-dialog/pilot-employment-record-assignment-to-duty-add-dialog.component';
import { PilotEmploymentRecordAssignmentToDutyEditDialogComponent } from '../pilot-employment-record-assignment-to-duty-edit-dialog/pilot-employment-record-assignment-to-duty-edit-dialog.component';

@Component({
  selector: 'fl-pilot-employment-record-assignment-to-duties-list',
  templateUrl: './pilot-employment-record-assignment-to-duties-list.component.html',
  styleUrls: ['./pilot-employment-record-assignment-to-duties-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PilotEmploymentRecordAssignmentToDutiesListComponent implements OnInit, OnDestroy {
  @Input()
  public employmentRecordId: string;

  public assignmentToDuties$: Observable<AssignmentToDutyEntity[]>;

  public displayedColumns = [
    'startDate',
    'endDate',
    'aircraftMakeModelId',
    'dutyPosition',
    'processingState',
    'lastProcessedDate',
    'processingError',
    'prdSourceSystemId',
    'actions',
  ];

  public _pageRefresh$ = new BehaviorSubject<{}>({});
  public _onDestroy$ = new Subject<void>();

  public loadingKey = 'pilot-employment-record-assignment-to-duties-table';

  public aircraftMakeModelCode$ = new ReplaySubject<string>(1);

  constructor(
    private _assignmentToDuty: AssignmentToDutyHttpClient,
    private _loading: FlLoadingService,
    private _dialogService: TdDialogService,
    private _dialog: MatDialog,
    private _snackBar: MatSnackBar,
    private _aircraft: AircraftMakeModelHttpClient,
    private _store: Store<AppState>,
  ) {
    const assignmentToDutyResults$ = combineLatest(this._pageRefresh$).pipe(
      switchMap(() => {
        this.markAsLoading();
        return this._assignmentToDuty.getByEmploymentRecordId({
          employmentRecordId: this.employmentRecordId,
        });
      }),
      tap(() => this.markAsLoaded()),
      share(),
    );

    this.assignmentToDuties$ = assignmentToDutyResults$.pipe(
      map((r) =>
        r.results.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()),
      ),
    );
  }

  public ngOnInit() {
    this.loadingKey += this.employmentRecordId;
  }

  public ngOnDestroy() {
    this._onDestroy$.next();
  }

  public markAsLoading() {
    this._loading.register(this.loadingKey);
  }

  public markAsLoaded() {
    this._loading.resolveAll(this.loadingKey);
  }

  public edit(assignmentToDuty: AssignmentToDutyViewModel) {
    const dialogRef = this._dialog.open(PilotEmploymentRecordAssignmentToDutyEditDialogComponent, {
      data: {
        employmentRecordId: this.employmentRecordId,
        assignmentToDuty,
      },
      disableClose: true,
    });
    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(async () => {
        this.markAsLoading();
        await sleep(2000);
        this._pageRefresh$.next({});
      });
  }

  public async delete(assignmentToDutyId: string) {
    const result = await this._dialogService
      .openConfirm({
        title: 'Delete Assignment to Duty?',
        message: 'Are you sure you wish to delete this assignment to duty?',
        acceptButton: 'Delete',
        cancelButton: 'Cancel',
      })
      .afterClosed()
      .toPromise();
    if (result === true) {
      try {
        await this._assignmentToDuty.delete(assignmentToDutyId).toPromise();
        this.markAsLoading();
        await sleep(2000);
        this._snackBar.open('Succesfully deleted assignment to duty.', undefined, {
          duration: 2000,
        });
        this._pageRefresh$.next({});
      } catch (err) {
        this._snackBar.open('Error deleting assignment to duty.', undefined, {
          duration: 2000,
        });

        throw err;
      }
    }
  }

  public add() {
    const dialogRef = this._dialog.open(PilotEmploymentRecordAssignmentToDutyAddDialogComponent, {
      data: {
        employmentRecordId: this.employmentRecordId,
      },
      disableClose: true,
    });
    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(async () => {
        this.markAsLoading();
        await sleep(2000);
        this._pageRefresh$.next({});
      });
  }

  public refresh() {
    this._pageRefresh$.next({});
  }
}
